import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { ROTULO_DA_FAMILIA_DE_MATERIAL, chaveDoProprio, type AbrangenciaDoServico } from "@bookdental/schemas";
import { CampoAbrangencia } from "@/components/catalogo/CampoCategoria";
import { MateriaisECoresDoServico, type OfertaDeMaterial } from "@/components/catalogo/MateriaisECoresDoServico";
import { porFamilia, type EscalaDaBase, type MaterialDaBase, type MaterialProprio, type ServicoEscolhido } from "./tipos";
import { estadoInicialDoGrupo, materiaisDoGrupo } from "./grupo";

export type ResultadoDoGrupo = {
  materiaisPorServico: Record<string, string[]>;
  escalasPorMaterial: Record<string, string[]>;
  /** Só quando a pessoa escolheu: vale pra todos. */
  abrangencia: AbrangenciaDoServico | null;
};

/**
 * Vários serviços de uma vez (a categoria inteira ou os selecionados) — pedido do Jun. Mesmo miolo
 * do diálogo de um serviço; o que só parte deles tem aparece "em alguns" e, sem a pessoa mexer,
 * continua só neles.
 */
export function ConfigurarGrupoDialog({
  servicos,
  onFechar,
  mapa,
  padraoDe,
  abrangenciaDe,
  materiais,
  proprios,
  onNovoProprio,
  escalas,
  escalasPorMaterial,
  onSalvar,
}: {
  servicos: ServicoEscolhido[] | null;
  onFechar: () => void;
  mapa: Record<string, string[]>;
  padraoDe: (chave: string) => string[];
  abrangenciaDe: (chave: string) => AbrangenciaDoServico;
  materiais: MaterialDaBase[];
  proprios: MaterialProprio[];
  onNovoProprio: (nome: string, familia?: string) => void;
  escalas: EscalaDaBase[];
  escalasPorMaterial: Record<string, string[]>;
  onSalvar: (chaves: string[], resultado: ResultadoDoGrupo) => void;
}) {
  const chaves = (servicos ?? []).map((s) => s.chave);
  const [marcados, setMarcados] = useState<string[]>([]);
  const [parciais, setParciais] = useState<string[]>([]);
  const [escalasAqui, setEscalasAqui] = useState<Record<string, string[]>>({});
  const [abrangencia, setAbrangencia] = useState<AbrangenciaDoServico | null>(null);
  const [tocouAbrangencia, setTocouAbrangencia] = useState(false);

  useEffect(() => {
    if (!servicos) return;
    const inicio = estadoInicialDoGrupo(chaves, mapa, abrangenciaDe);
    setMarcados(inicio.comuns);
    setParciais(inicio.parciais);
    setAbrangencia(inicio.abrangencia);
    setTocouAbrangencia(false);
    setEscalasAqui(escalasPorMaterial);
    // Só ao abrir: depois quem manda é o que a pessoa mexe aqui.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaves.join("|")]);

  // Material que a pessoa clicou deixa de ser "em alguns": passa a valer pra todos ou pra nenhum.
  const mudarMarcados = (novos: string[]) => {
    const mexidos = [...novos.filter((m) => !marcados.includes(m)), ...marcados.filter((m) => !novos.includes(m))];
    setParciais(parciais.filter((m) => !mexidos.includes(m)));
    setMarcados(novos);
  };

  const aVista = new Set([...chaves.flatMap(padraoDe), ...marcados, ...parciais]);
  const daBase = porFamilia(materiais);
  const oferta = (m: MaterialDaBase): OfertaDeMaterial => ({ id: m.id, label: m.nome, grupo: ROTULO_DA_FAMILIA_DE_MATERIAL[m.familia ?? ""] ?? "Outros" });
  const ofertas: OfertaDeMaterial[] = [
    ...daBase.filter((m) => aVista.has(m.id)).map(oferta),
    ...proprios.map((p) => ({ id: chaveDoProprio(p.nome), label: p.nome, grupo: p.familia ?? "Criados por você" })),
  ];
  const outras = daBase.filter((m) => !aVista.has(m.id)).map(oferta);

  return (
    <Dialog open={!!servicos} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="flex max-h-[88dvh] max-w-2xl flex-col gap-3 overflow-y-auto border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle>
            {chaves.length} {chaves.length === 1 ? "serviço" : "serviços"} de uma vez
          </DialogTitle>
          <DialogDescription className="line-clamp-2">{(servicos ?? []).map((s) => s.nome).join(", ")}</DialogDescription>
        </DialogHeader>

        <CampoAbrangencia
          valor={abrangencia}
          onChange={(v) => {
            setAbrangencia(v);
            setTocouAbrangencia(true);
          }}
        />

        <MateriaisECoresDoServico
          ofertas={ofertas}
          outras={outras}
          materialIds={marcados}
          onMaterialIds={mudarMarcados}
          parciais={parciais}
          escalasPorMaterial={escalasAqui}
          onEscalasPorMaterial={setEscalasAqui}
          escalas={escalas.map((e) => ({ codigo: e.codigo, rotulo: e.nome }))}
          onNovoMaterial={(nome, familia) => {
            const chave = chaveDoProprio(nome);
            if (!proprios.some((p) => p.nome.toLowerCase() === nome.toLowerCase())) onNovoProprio(nome, familia);
            if (!marcados.includes(chave)) mudarMarcados([...marcados, chave]);
          }}
        />

        <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-3 dark:border-border">
          <button
            type="button"
            onClick={onFechar}
            className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-sm font-medium text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() =>
              onSalvar(chaves, {
                materiaisPorServico: materiaisDoGrupo(chaves, mapa, marcados, parciais),
                escalasPorMaterial: escalasAqui,
                abrangencia: tocouAbrangencia ? abrangencia : null,
              })
            }
            className="inline-flex h-9 items-center rounded-md bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
          >
            Aplicar em {chaves.length}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
